/**
 * `canary api-catalog <sources|endpoints> <verb>` — CRUD for API catalog
 * sources (OpenAPI/Postman imports) and endpoints (aka api_requests).
 *
 * Lets a coding agent seed a catalog from scratch, search for endpoints to
 * wire into API sequences, and curate existing entries.
 *
 * @module ApiCatalogCLI
 */

import fs from 'node:fs/promises';
import process from 'node:process';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

/* ── Arg helpers ───────────────────────────────────────────────────────── */

function getArg(argv: string[], key: string): string | undefined {
  const prefix = `${key}=`;
  const eq = argv.find((a) => a.startsWith(prefix));
  if (eq) return eq.slice(prefix.length);
  return getArgValue(argv, key);
}

function wantsJson(argv: string[]): boolean {
  if (hasFlag(argv, '--json')) return true;
  if (hasFlag(argv, '--text')) return false;
  return !process.stdout.isTTY;
}

function emit(argv: string[], textFallback: string, json: unknown): void {
  if (wantsJson(argv)) {
    console.log(JSON.stringify(json, null, 2));
  } else {
    console.log(textFallback);
  }
}

async function readJsonInput(argv: string[]): Promise<unknown> {
  const fromFile = getArg(argv, '--from-file');
  let raw: string;
  if (fromFile) {
    raw = await fs.readFile(fromFile, 'utf-8');
  } else if (hasFlag(argv, '--from-stdin') || !process.stdin.isTTY) {
    raw = await readStdin();
  } else {
    throw new Error('Provide --from-file <path> or pipe JSON on stdin.');
  }
  try {
    return JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON input.');
  }
}

function readStdin(): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunks: Buffer[] = [];
    process.stdin.on('data', (chunk) => {
      chunks.push(typeof chunk === 'string' ? Buffer.from(chunk) : chunk);
    });
    process.stdin.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
    process.stdin.on('error', reject);
  });
}

function positional(argv: string[]): string[] {
  return argv.filter((a) => !a.startsWith('-'));
}

/* ── Types ─────────────────────────────────────────────────────────────── */

interface GenericResponse<T = unknown> {
  ok: boolean;
  error?: string;
  data?: T;
  pagination?: unknown;
  filters?: unknown;
  meta?: unknown;
}

/* ── Entry point ───────────────────────────────────────────────────────── */

export async function runApiCatalog(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  const [group, ...rest] = argv;

  if (!group || group === 'help' || hasFlag(argv, '--help', '-h')) {
    printHelp();
    return;
  }

  switch (group) {
    case 'sources':
      await runSources(rest, apiUrl, token);
      break;
    case 'endpoints':
      await runEndpoints(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown group: ${group}`);
      printHelp();
      process.exit(1);
  }
}

function printHelp() {
  console.error(
    [
      'Usage: canary api-catalog <sources|endpoints> <verb> [options]',
      '',
      'Sources (OpenAPI / Postman imports):',
      '  sources list                                 list catalog sources',
      '  sources get <id>                             fetch a source',
      '  sources create --from-file <f>               create source + run initial import',
      '  sources patch <id> --from-file <f>           update source metadata',
      '  sources refresh <id>                         re-sync a source',
      '  sources delete <id>                          delete a source',
      '',
      'Endpoints (individual requests):',
      '  endpoints list [--search <q>] [--application <id>] [--method <m>] [--page <n>]',
      '                                              list/search endpoints',
      '  endpoints get <id>                           fetch one endpoint',
      '  endpoints create --from-file <f>             create a manual endpoint',
      '                                              body: { method, url, propertyId, name? }',
      '  endpoints patch <id> --from-file <f>         update endpoint fields (name, schemas)',
      '  endpoints delete <id>                        delete one endpoint',
      '  endpoints delete-many --ids a,b,c            delete many endpoints',
      '  endpoints test <id>                          run the endpoint against its application',
      '  endpoints domains                            list distinct domains',
      '',
      'Global options:',
      '  --json                                      force JSON output',
      '  --text                                      force pretty-text output',
      '',
      'Input for create/patch: JSON via --from-file or stdin. Field shapes match the API route',
      'schemas (see apps/api/src/api-catalog/*.ts for the authoritative definitions).',
    ].join('\n'),
  );
}

/* ── Sources ───────────────────────────────────────────────────────────── */

async function runSources(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [verb, ...rest] = argv;
  switch (verb) {
    case 'list':
      await sourcesList(rest, apiUrl, token);
      break;
    case 'get':
      await sourcesGet(rest, apiUrl, token);
      break;
    case 'create':
      await sourcesCreate(rest, apiUrl, token);
      break;
    case 'patch':
      await sourcesPatch(rest, apiUrl, token);
      break;
    case 'refresh':
      await sourcesRefresh(rest, apiUrl, token);
      break;
    case 'delete':
      await sourcesDelete(rest, apiUrl, token);
      break;
    default:
      printHelp();
      process.exit(verb ? 1 : 0);
  }
}

async function sourcesList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const result = await apiRequest<GenericResponse<unknown[]>>(
    apiUrl,
    token,
    'GET',
    '/api-catalog-sources',
  );
  if (!result.ok) exitErr(result.error);
  const sources = (result.data ?? []) as Array<Record<string, unknown>>;
  const text =
    sources.length === 0
      ? 'No sources.'
      : sources
          .map((s) => {
            const id = String(s.id ?? '?');
            const name = String(s.name ?? '(unnamed)');
            const format = String(s.format ?? '?');
            const count = s.lastEndpointCount ?? '-';
            const status = String(s.lastSyncStatus ?? '?');
            return `${id}  ${name.padEnd(30)} ${format.padEnd(10)} ${count} endpoints (${status})`;
          })
          .join('\n');
  emit(argv, text, result);
}

async function sourcesGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing source ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'GET',
    `/api-catalog-sources/${id}`,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, JSON.stringify(result.data, null, 2), result);
}

async function sourcesCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  let body: unknown;
  try {
    body = await readJsonInput(argv);
  } catch (err) {
    exitErr(err instanceof Error ? err.message : String(err));
  }
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'POST',
    '/api-catalog-sources',
    body as Record<string, unknown>,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, 'Source created.', result);
}

async function sourcesPatch(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing source ID.');
  let body: unknown;
  try {
    body = await readJsonInput(argv);
  } catch (err) {
    exitErr(err instanceof Error ? err.message : String(err));
  }
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'PATCH',
    `/api-catalog-sources/${id}`,
    body as Record<string, unknown>,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Source ${id} patched.`, result);
}

async function sourcesRefresh(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing source ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'POST',
    `/api-catalog-sources/${id}/refresh`,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Source ${id} refreshed.`, result);
}

async function sourcesDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing source ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'DELETE',
    `/api-catalog-sources/${id}`,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Source ${id} deleted.`, result);
}

/* ── Endpoints ─────────────────────────────────────────────────────────── */

async function runEndpoints(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [verb, ...rest] = argv;
  switch (verb) {
    case 'list':
      await endpointsList(rest, apiUrl, token);
      break;
    case 'get':
      await endpointsGet(rest, apiUrl, token);
      break;
    case 'create':
      await endpointsCreate(rest, apiUrl, token);
      break;
    case 'patch':
      await endpointsPatch(rest, apiUrl, token);
      break;
    case 'delete':
      await endpointsDelete(rest, apiUrl, token);
      break;
    case 'delete-many':
      await endpointsDeleteMany(rest, apiUrl, token);
      break;
    case 'test':
      await endpointsTest(rest, apiUrl, token);
      break;
    case 'domains':
      await endpointsDomains(rest, apiUrl, token);
      break;
    default:
      printHelp();
      process.exit(verb ? 1 : 0);
  }
}

async function endpointsList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const params = new URLSearchParams();
  const search = getArg(argv, '--search');
  const propertyId = getArg(argv, '--application') ?? getArg(argv, '--property');
  const method = getArg(argv, '--method');
  const page = getArg(argv, '--page');
  const pageSize = getArg(argv, '--page-size');
  if (search) params.set('search', search);
  if (propertyId) params.set('propertyId', propertyId);
  if (method) params.set('method', method);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);

  const qs = params.toString();
  const result = await apiRequest<GenericResponse<Array<Record<string, unknown>>>>(
    apiUrl,
    token,
    'GET',
    `/api-requests${qs ? `?${qs}` : ''}`,
  );
  if (!result.ok) exitErr(result.error);

  const items = (result.data ?? []) as Array<Record<string, unknown>>;
  const text =
    items.length === 0
      ? 'No endpoints.'
      : items
          .map((ep) => {
            const id = String(ep.id ?? '?');
            const method = String(ep.method ?? '?').toUpperCase().padEnd(6);
            const url = String(ep.url ?? ep.path ?? '');
            const name = ep.name ? `  — ${String(ep.name)}` : '';
            return `${id}  ${method} ${url}${name}`;
          })
          .join('\n');
  emit(argv, text, result);
}

async function endpointsGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing endpoint ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'GET',
    `/api-requests/${id}`,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, JSON.stringify(result.data, null, 2), result);
}

async function endpointsCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  let body: unknown;
  try {
    body = await readJsonInput(argv);
  } catch (err) {
    exitErr(err instanceof Error ? err.message : String(err));
  }
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'POST',
    '/api-requests',
    body as Record<string, unknown>,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, 'Endpoint created.', result);
}

async function endpointsPatch(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing endpoint ID.');
  let body: unknown;
  try {
    body = await readJsonInput(argv);
  } catch (err) {
    exitErr(err instanceof Error ? err.message : String(err));
  }
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'PATCH',
    `/api-requests/${id}`,
    body as Record<string, unknown>,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Endpoint ${id} patched.`, result);
}

async function endpointsDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing endpoint ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'DELETE',
    '/api-requests/bulk',
    { ids: [id] },
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Endpoint ${id} deleted.`, result);
}

async function endpointsDeleteMany(
  argv: string[],
  apiUrl: string,
  token: string,
): Promise<void> {
  const idsArg = getArg(argv, '--ids');
  if (!idsArg) exitErr('Missing --ids <a,b,c>.');
  const ids = idsArg
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (ids.length === 0) exitErr('No IDs provided.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'DELETE',
    '/api-requests/bulk',
    { ids },
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, `Deleted ${ids.length} endpoint(s).`, result);
}

async function endpointsTest(argv: string[], apiUrl: string, token: string): Promise<void> {
  const [id] = positional(argv);
  if (!id) exitErr('Missing endpoint ID.');
  const result = await apiRequest<GenericResponse>(
    apiUrl,
    token,
    'POST',
    `/api-requests/${id}/test`,
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, JSON.stringify(result.data, null, 2), result);
}

async function endpointsDomains(argv: string[], apiUrl: string, token: string): Promise<void> {
  const result = await apiRequest<GenericResponse<unknown>>(
    apiUrl,
    token,
    'GET',
    '/api-requests/domains',
  );
  if (!result.ok) exitErr(result.error);
  emit(argv, JSON.stringify(result.data, null, 2), result);
}

/* ── Error helper ──────────────────────────────────────────────────────── */

function exitErr(msg: string | undefined): never {
  console.error(`Error: ${msg ?? 'Unknown error'}`);
  process.exit(1);
}
